/**
 * "Grades" tab of the cohort detail view (F3-B): gradebook grid (students ×
 * items + totals) with inline editing of assignment scores (→ bulk
 * endpoint), category/item editor (local-first), and a cohort CLOSE button
 * (with a preview of finals). The matrix is computed by the SERVER.
 */
import { type Component, createMemo, createResource, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Plus, Trash2 } from "@fleet/icons";
import {
  createGradeCategoryMutation,
  createGradeItemMutation,
  deleteGradeCategoryMutation,
  updateGradeCategoryMutation,
} from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { waitForUploadQueueDrain } from "@/lib/powersync/wait-upload";
import { Button } from "@/components/ui/button";
import { showToast } from "@/components/ui/toast";
import { ExportButton } from "@/features/io/components/export-button";
import { useCourseAssignments, useGradeCategories } from "../hooks/use-assessment";
import { useActiveGradingScale } from "../hooks/use-grading-scale";
import { closeCohort, fetchGradebook, submitBulkGrades } from "./api";

const field =
  "w-full rounded-lg border-2 border-border bg-field-background px-2 py-1 text-sm focus-visible:border-primary focus-visible:outline-none";

export const GradesTab: Component<{
  cohortId: string;
  organizationId: string;
  courseId?: string;
}> = (props) => {
  const { t } = useI18n();
  const drizzle = useDrizzle();
  const powerSync = usePowerSyncContext();
  const categories = useGradeCategories(() => props.cohortId);
  const assignments = useCourseAssignments(() => props.courseId);
  const scale = useActiveGradingScale({
    organizationId: () => props.organizationId,
    courseId: () => props.courseId,
    cohortId: () => props.cohortId,
  });

  const [gradebook, { refetch }] = createResource(
    () => props.cohortId,
    (id) => fetchGradebook(id),
  );

  const [closing, setClosing] = createSignal(false);

  const itemById = createMemo(() => {
    const map = new Map<string, { source_kind: string; source_ref: string | null }>();
    for (const it of gradebook()?.items ?? []) {
      map.set(it.id, { source_kind: it.source_kind, source_ref: it.source_ref });
    }
    return map;
  });

  // Inline editing of an ASSIGNMENT score → bulk endpoint (server-authoritative).
  const saveScore = async (itemId: string, userId: string, raw: string) => {
    const meta = itemById().get(itemId);
    if (!meta || meta.source_kind !== "assignment" || !meta.source_ref) {
      return;
    }
    const score = raw.trim() === "" ? null : Number(raw);
    if (score != null && Number.isNaN(score)) {
      return;
    }
    try {
      await submitBulkGrades(props.cohortId, [
        { assignment_id: meta.source_ref, user_id: userId, score, status: "graded" },
      ]);
      void refetch();
    } catch {
      showToast(t(msg`Could not save the grade`), "error");
    }
  };

  const onClose = async () => {
    setClosing(true);
    try {
      const summary = await closeCohort(props.cohortId);
      const { completed } = summary;
      const { certificates } = summary;
      showToast(
        t(msg`Cohort closed: ${completed} finalized, ${certificates} certificates`),
        "success",
      );
      void refetch();
    } catch {
      showToast(t(msg`Could not close the cohort`), "error");
    } finally {
      setClosing(false);
    }
  };

  // ── Gradebook editors (local-first) ──
  // local-first→REST seam (F4-D): the category/item is written through the
  // write-path (upload queue) but the GRID is a REST resource that reads
  // from the server. Without draining the queue and refetching, the
  // newly-added column never shows up (until the next incidental refetch).
  const db = () => drizzle();
  const syncThenRefetch = async () => {
    const ps = powerSync.db();
    if (ps) {
      await waitForUploadQueueDrain(ps);
    }
    void refetch();
  };
  const addCategory = async () => {
    const d = db();
    if (d) {
      await createGradeCategoryMutation(d, {
        cohortId: props.cohortId,
        organizationId: props.organizationId,
      });
      await syncThenRefetch();
    }
  };
  const addItem = async (
    sourceKind: "assignment" | "auto_blocks" | "attendance" | "manual",
    sourceRef?: string,
    name?: string,
    maxScore?: number,
  ) => {
    const d = db();
    if (d) {
      await createGradeItemMutation(d, {
        cohortId: props.cohortId,
        organizationId: props.organizationId,
        sourceKind,
        sourceRef,
        name,
        // Snapshot of the ASSIGNMENT's max when the item comes from one:
        // without it, an 18/20 score used to be scaled as 18/100 (final
        // 3.6). If there's no assignment behind it (manual item), the max is
        // DERIVED from the active scale.
        maxScore,
        scale: scale(),
      });
      await syncThenRefetch();
    }
  };

  return (
    <div class="space-y-4" data-testid="cohort-gradebook">
      {/* F4-B: export of the grades summary (io.gradebook) */}
      <div class="flex justify-end">
        <ExportButton
          entityKey="io.gradebook"
          orgId={props.organizationId}
          filters={{ cohort_id: props.cohortId }}
        />
      </div>

      {/* Category editor (weights + drop_lowest) */}
      <div class="rounded-2xl border border-border p-3">
        <div class="mb-2 flex items-center justify-between">
          <h3 class="text-sm font-bold">{t(msg`Categories & weights`)}</h3>
          <button
            type="button"
            class="inline-flex items-center gap-1 text-xs text-accent"
            data-testid="add-grade-category"
            onClick={() => void addCategory()}
          >
            <Plus class="h-3 w-3" /> {t(msg`Category`)}
          </button>
        </div>
        <For each={categories.data ?? []}>
          {(c) => (
            <div class="mb-1 flex items-center gap-2" data-testid="grade-category-row">
              <input
                class={field}
                value={c.name ?? ""}
                onChange={(e) => {
                  const d = db();
                  if (d) {
                    void updateGradeCategoryMutation(
                      d,
                      c.id,
                      { name: e.currentTarget.value },
                      props.organizationId,
                    ).catch(() => {
                      showToast(t(msg`Could not save the category`), "error");
                    });
                  }
                }}
              />
              <input
                type="number"
                class={field}
                style={{ "max-width": "5rem" }}
                data-testid="grade-category-weight"
                value={c.weight ?? 1}
                title={t(msg`Weight`)}
                onChange={(e) => {
                  const d = db();
                  if (d) {
                    void updateGradeCategoryMutation(
                      d,
                      c.id,
                      { weight: Number(e.currentTarget.value) || 0 },
                      props.organizationId,
                    ).catch(() => {
                      showToast(t(msg`Could not save the category`), "error");
                    });
                  }
                }}
              />
              <input
                type="number"
                class={field}
                style={{ "max-width": "5rem" }}
                value={c.drop_lowest ?? 0}
                title={t(msg`Drop lowest`)}
                onChange={(e) => {
                  const d = db();
                  if (d) {
                    void updateGradeCategoryMutation(
                      d,
                      c.id,
                      { drop_lowest: Number(e.currentTarget.value) || 0 },
                      props.organizationId,
                    ).catch(() => {
                      showToast(t(msg`Could not save the category`), "error");
                    });
                  }
                }}
              />
              <button
                type="button"
                onClick={() => {
                  const d = db();
                  if (d) {
                    void deleteGradeCategoryMutation(d, c.id, props.organizationId).catch(() => {
                      showToast(t(msg`Could not delete the category`), "error");
                    });
                  }
                }}
                class="text-muted-foreground hover:text-danger"
              >
                <Trash2 class="h-4 w-4" />
              </button>
            </div>
          )}
        </For>
      </div>

      {/* Add grade columns */}
      <div class="flex flex-wrap gap-2 text-xs">
        <button
          type="button"
          class="rounded-lg bg-muted px-2 py-1"
          data-testid="add-item-auto"
          onClick={() => void addItem("auto_blocks", undefined, t(msg`Lessons`))}
        >
          + {t(msg`Auto (lessons)`)}
        </button>
        <button
          type="button"
          class="rounded-lg bg-muted px-2 py-1"
          data-testid="add-item-attendance"
          onClick={() => void addItem("attendance", undefined, t(msg`Attendance`))}
        >
          + {t(msg`Attendance`)}
        </button>
        <For each={assignments.data ?? []}>
          {(a) => (
            <button
              type="button"
              class="rounded-lg bg-muted px-2 py-1"
              data-testid="add-item-assignment"
              onClick={() =>
                void addItem("assignment", a.id, a.title ?? undefined, a.max_score ?? undefined)
              }
            >
              + {a.title}
            </button>
          )}
        </For>
      </div>

      {/* Gradebook grid */}
      <Show
        when={gradebook()}
        fallback={<p class="text-sm text-muted-foreground">{t(msg`Loading gradebook…`)}</p>}
      >
        {(gb) => (
          <div class="overflow-x-auto rounded-2xl border border-border">
            <table class="w-full text-sm" data-testid="gradebook-table">
              <thead>
                <tr class="border-b border-border bg-muted/30">
                  <th class="p-2 text-left">{t(msg`Student`)}</th>
                  <For each={gb().items}>
                    {(it) => (
                      <th class="p-2 text-center text-xs" data-testid="gradebook-col">
                        {it.name}
                        <div class="font-normal text-muted-foreground">/{it.max_score}</div>
                      </th>
                    )}
                  </For>
                  <th class="p-2 text-center">{t(msg`Final`)}</th>
                </tr>
              </thead>
              <tbody>
                <For each={gb().students}>
                  {(st) => (
                    <tr class="border-b border-border/60" data-testid="gradebook-row">
                      <td class="p-2" data-testid="gradebook-student">
                        {st.name ?? st.email ?? st.user_id}
                      </td>
                      <For each={st.cells}>
                        {(cell) => {
                          const meta = itemById().get(cell.item_id);
                          const editable = meta?.source_kind === "assignment";
                          return (
                            <td class="p-1 text-center">
                              <Show
                                when={editable}
                                fallback={
                                  <span class="text-muted-foreground">
                                    {cell.score == null ? "—" : Math.round(cell.score * 10) / 10}
                                  </span>
                                }
                              >
                                <input
                                  type="number"
                                  class={`${field} text-center`}
                                  style={{ "max-width": "4.5rem" }}
                                  data-testid="gradebook-cell-input"
                                  value={cell.score ?? ""}
                                  onChange={(e) =>
                                    void saveScore(cell.item_id, st.user_id, e.currentTarget.value)
                                  }
                                />
                              </Show>
                            </td>
                          );
                        }}
                      </For>
                      <td class="p-2 text-center font-bold" data-testid="gradebook-final">
                        <span class={st.passing ? "text-joy-mint" : "text-danger"}>
                          {st.final_display}
                        </span>
                      </td>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </div>
        )}
      </Show>

      {/* Cohort close with preview */}
      <div class="rounded-2xl border-2 border-border p-4">
        <h3 class="mb-1 text-sm font-bold">{t(msg`Close cohort`)}</h3>
        <p class="mb-2 text-xs text-muted-foreground">
          {t(
            msg`Seals final grades, completes enrollments and issues certificates for those who pass.`,
          )}
        </p>
        <Show when={gradebook()}>
          {(gb) => (
            <p class="mb-2 text-xs" data-testid="close-preview">
              {t(msg`Preview:`)} {gb().students.filter((s) => s.passing).length}/
              {gb().students.length} {t(msg`passing`)} · {t(msg`scale`)} {gb().scheme_key}
            </p>
          )}
        </Show>
        <Button
          data-testid="close-cohort-button"
          disabled={closing()}
          onClick={() => void onClose()}
        >
          {closing() ? t(msg`Closing…`) : t(msg`Close cohort`)}
        </Button>
      </div>
    </div>
  );
};
